import * as React from 'react';
import { SideNavButton, SideNavButtonIcon } from '../SideNavButton';

export type SideNavBarIcon = Exclude<SideNavButtonIcon, 'home'>;

export interface SideNavBarProps {
  /**
   * Whether the navigation panel is open (showing title and children)
   * @default false
   */
  isOpen?: boolean;
  /**
   * Title displayed in the header when open (e.g., "Attendance")
   */
  title?: string;
  /**
   * Navigation items to display when open (typically SideNavItem components)
   */
  children?: React.ReactNode;
  /**
   * The currently selected/active icon (if any)
   */
  selectedIcon?: SideNavBarIcon;
  /**
   * Click handler for icon buttons - receives the icon type that was clicked
   */
  onIconClick?: (icon: SideNavBarIcon) => void;
  /**
   * Custom className for the container
   */
  className?: string;
  /**
   * Custom style for the container
   */
  style?: React.CSSProperties;
}

/**
 * SideNavBar component - Arbor Design System
 *
 * A vertical side navigation bar with fixed icon layout.
 * Icons are grouped with dividers:
 * - Top: Side menu
 * - Middle: Favourite, Notifications, Calendar
 * - Lower: Emergency alert
 * - Bottom (pushed down): Help
 *
 * When open, displays a content panel with a title header and navigation items.
 */
export const SideNavBar: React.FC<SideNavBarProps> = ({
  isOpen = false,
  title,
  children,
  selectedIcon,
  onIconClick,
  className,
  style,
}) => {
  const wrapperStyles: React.CSSProperties = {
    display: 'flex',
    height: '100%',
    ...style,
  };

  const iconBarStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    padding: '8px',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    height: '100%',
    width: '58px',
    boxSizing: 'border-box',
    flexShrink: 0,
  };

  const topSectionStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  };

  const dividerStyles: React.CSSProperties = {
    width: '24.5px',
    height: '1px',
    backgroundColor: '#efefef',
  };

  const contentPanelStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderLeft: '1px solid #f8f8f8',
    height: '100%',
    width: '232px',
    boxSizing: 'border-box',
    flexShrink: 0,
    minWidth: 0,
    minHeight: 0,
  };

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: '62px',
    paddingLeft: '20px',
    paddingRight: '16px',
    paddingTop: '4px',
    paddingBottom: '4px',
    borderBottom: '1px solid #f8f8f8',
    boxSizing: 'border-box',
    flexShrink: 0,
  };

  const titleStyles: React.CSSProperties = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    color: '#2f2f2f',
    lineHeight: 1.5,
  };

  const navListStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: '8px',
    boxSizing: 'border-box',
  };

  const handleClick = (icon: SideNavBarIcon) => {
    onIconClick?.(icon);
  };

  return (
    <div className={className} style={wrapperStyles}>
      {/* Icon Bar */}
      <div style={iconBarStyles}>
        {/* Top section with icon groups */}
        <div style={topSectionStyles}>
          {/* Side menu */}
          <SideNavButton
            icon="side-menu"
            selected={selectedIcon === 'side-menu'}
            onClick={() => handleClick('side-menu')}
            aria-label="Side menu"
          />

          {/* Divider */}
          <div style={dividerStyles} />

          {/* Main navigation group */}
          <SideNavButton
            icon="favourite"
            selected={selectedIcon === 'favourite'}
            onClick={() => handleClick('favourite')}
            aria-label="Favourites"
          />
          <SideNavButton
            icon="notifications"
            selected={selectedIcon === 'notifications'}
            onClick={() => handleClick('notifications')}
            aria-label="Notifications"
          />
          <SideNavButton
            icon="calendar"
            selected={selectedIcon === 'calendar'}
            onClick={() => handleClick('calendar')}
            aria-label="Calendar"
          />

          {/* Divider */}
          <div style={dividerStyles} />

          {/* Emergency alert */}
          <SideNavButton
            icon="emergency-alert"
            selected={selectedIcon === 'emergency-alert'}
            onClick={() => handleClick('emergency-alert')}
            aria-label="Emergency alerts"
          />
        </div>

        {/* Bottom section - Help */}
        <SideNavButton
          icon="help"
          selected={selectedIcon === 'help'}
          onClick={() => handleClick('help')}
          aria-label="Help"
        />
      </div>

      {/* Content Panel (only shown when open) */}
      {isOpen && (
        <div style={contentPanelStyles}>
          {/* Header with title */}
          {title && (
            <div style={headerStyles}>
              <span style={titleStyles}>{title}</span>
            </div>
          )}

          {/* Navigation list container */}
          <div style={navListStyles}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

SideNavBar.displayName = 'SideNavBar';
